import { formatDate, formatMoney, t } from "@hakili/i18n";
import { ERROR_FIELD_BORDER, ERROR_TEXT } from "@hakili/ui";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import type { ReactElement } from "react";

import type { IssuedProforma } from "../session/vehicle-papers.js";
import { HeldCompounds } from "./held-compounds.js";

/**
 * `P1-DLW-051` (the Accueil, A47) and its follow-up: **the one-tap proforma is never silent**, said the same way on the Accueil and on the list.
 *
 * - **On a refusal:** the door's sentence VERBATIM in the kit's error tone with its alert icon, on the task or the row, which stays; a second tap clears it before it speaks again.
 * - **On success:** « Facture proforma émise », the client and the car, and the door's own answer in the catalogue's sentence (the number, the version, the validity date through `formatDate`, the amounts with U+00A0 before FCFA); the task or the row
 *   leaves once the dossiers are read again.
 *
 * The refusal wears the kit's error tone (`ERROR_TEXT`, `ERROR_FIELD_BORDER`, #902), read from the kit and never written here; it is no longer the warn notice's amber.
 */
const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

/** The door's answer, in the catalogue's own sentence (`dealer.vehicules.emiseLigne`). */
export const issuedLineOf = (issued: IssuedProforma): string =>
  fill("dealer.vehicules.emiseLigne", {
    numero: issued.numberLabel,
    version: String(issued.version),
    date: formatDate(new Date(issued.validUntil)),
    prix: formatMoney(issued.priceTtcAmount),
    ht: formatMoney(issued.priceHtAmount),
    tva: formatMoney(issued.tvaAmount),
  });

export function RefusalNotice(props: { readonly message: string; readonly testId: string }): ReactElement {
  return (
    <div
      role="alert"
      className={`mt-2 flex items-start gap-2 rounded-xl border ${ERROR_FIELD_BORDER} bg-white px-3 py-2 text-left text-sm ${ERROR_TEXT}`}
      data-testid={props.testId}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        <HeldCompounds text={props.message} />
      </span>
    </div>
  );
}

export function IssuedNotice(props: {
  readonly who: string;
  readonly line: string;
  readonly testId: string;
}): ReactElement {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-gray-900"
      data-testid={props.testId}
    >
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
      <div className="min-w-0">
        <p className="m-0 font-semibold">{t("dealer.accueil.proformaEmise")}</p>
        <p className="m-0 mt-0.5">{props.who}</p>
        <p className="m-0 mt-0.5 text-gray-700">{props.line}</p>
      </div>
    </div>
  );
}
